import { Button, Form, Layout, Modal } from 'antd';
import { Content } from 'antd/es/layout/layout';
import { useContext, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { SDetailLayout } from 'app/components/StaycationUI';
import { AssetDetailTitle } from 'app/components/commons/AssetDetailTitle/AssetDetailTitle';
import { Delete } from 'app/components/commons/ExtraButtons/Delete/Delete';
import { Mode } from 'app/components/commons/Hotel/Types';
import { MenuCustom } from 'app/components/commons/MenuCustom/MenuCustom';
import { Spinner } from 'app/components/commons/Spinner/Spinner';
import { ConfigContext } from 'app/context/ConfigContext/ConfigContext';
import { useCountries } from 'app/hooks/data/useCountries';

import './Pack.scss';
import { PackForm } from './_components/PackForm';
import { usePackActions } from './hooks/usePackActions';
import { usePackData } from './hooks/usePackData';
import { MenuItemName } from './menuItem';

type PageContext = {
  isCreatePage: boolean;
  packId?: number;
};

type DisplayConfig = {
  title?: string;
  buttonSaveText?: string;
  showDelete?: boolean;
};

type MenuItem = {
  name: MenuItemName;
};

const MENU_ITEMS = [
  { name: MenuItemName.General },
  { name: MenuItemName.ExperiencesAndRooms },
  { name: MenuItemName.Limitations },
  { name: MenuItemName.Copywriting },
  { name: MenuItemName.Disclaimer },
  { name: MenuItemName.Medias },
];

const useHeaderLogic = ({
  isCreatePage,
  showDelete,
  packId,
}: PageContext & Pick<DisplayConfig, 'showDelete'>) => {
  const shouldShowEdit = !isCreatePage;
  const shouldShowCancel = !isCreatePage;
  const shouldShowDelete = !isCreatePage && showDelete;

  return {
    shouldShowEdit,
    shouldShowCancel,
    shouldShowDelete,
    deleteEntityDescription: `Package #${packId}`,
  };
};

export const Pack = () => {
  const location = useLocation<{ pathname?: string }>();
  const [menu, setMenu] = useState<MenuItem>(MENU_ITEMS[0]);
  const [pendingMenu, setPendingMenu] = useState<MenuItem | null>(null);
  const [showModal, setShowModal] = useState(false);

  const config = useContext(ConfigContext);
  const { data: countries } = useCountries();

  const packData = usePackData();
  const {
    packId,
    isCreatePage,
    hotel,
    initialValues,
    isDataLoaded,
    title,
    buttonSaveText,
    isFetching,
    fetchedPack,
  } = packData;

  const packActions = usePackActions({
    packId,
    isCreatePage,
    initialValues,
    isFetching,
    activeTab: menu.name,
  });

  const {
    form,
    mode,
    canSubmit,
    isLoading,
    onFinish,
    onCancel,
    handleDelete,
    handleSave,
    enterEditMode,
    setCanSubmit,
    enterViewMode,
  } = packActions;

  const {
    shouldShowEdit,
    shouldShowCancel,
    shouldShowDelete,
    deleteEntityDescription,
  } = useHeaderLogic({
    isCreatePage,
    packId,
    showDelete: true,
  });

  const handleMenuChange = (newMenu: MenuItem) => {
    const isDirty = mode === Mode.Edit && form.isFieldsTouched(false);

    if (isDirty) {
      setPendingMenu(newMenu);
      setShowModal(true);
    } else {
      setMenu(newMenu);
    }
  };

  const handleModalSave = async () => {
    await form.validateFields();

    const values = form.getFieldsValue();

    await onFinish(values);

    if (pendingMenu) {
      setMenu(pendingMenu);
    }

    setShowModal(false);
    setPendingMenu(null);
  };

  const handleModalDiscard = () => {
    form.resetFields();
    enterViewMode();

    if (pendingMenu) {
      setMenu(pendingMenu);
    }

    setShowModal(false);
    setPendingMenu(null);
  };

  const renderMenu = () => (
    <MenuCustom
      menuItems={MENU_ITEMS.filter(
        (item) =>
          (item.name !== MenuItemName.Medias || fetchedPack?.isDaytime) &&
          (item.name !== MenuItemName.Disclaimer || fetchedPack?.isDaytime)
      ).map((item) => ({
        ...item,
        disabled: !isDataLoaded,
      }))}
      selectedMenu={menu}
      setSelectedMenu={handleMenuChange}
    />
  );

  // Preview the package page on the website. Only for daytime packages
  // ("moments"), which have their own dedicated page at /{locale}/hotels/{slug}.
  const renderPreviewButton = () => {
    if (!fetchedPack || !fetchedPack.isDaytime || !fetchedPack.slug) {
      return null;
    }

    const locale =
      countries?.find((country) => country.id === hotel?.countryId)?.locale ??
      'fr';

    return (
      <div key={'preview-button'} className="preview-btn">
        <Button
          size="large"
          onClick={() =>
            window.open(
              `${config?.appUrl}/${locale}/hotels/${fetchedPack.slug}`,
              '_blank'
            )
          }
        >
          Preview
        </Button>
      </div>
    );
  };

  if (!packId && !isCreatePage) {
    return null;
  }

  return (
    <Layout className="master-page pack-page">
      <SDetailLayout
        title={
          <AssetDetailTitle
            assetTitle={title}
            hotelId={hotel?.id}
            hotelName={hotel?.name}
            isNewDataModel={true}
          />
        }
        mode={mode}
        onEdit={shouldShowEdit ? enterEditMode : undefined}
        onSave={handleSave}
        onCancel={shouldShowCancel ? onCancel : undefined}
        buttonSaveText={buttonSaveText}
        headerExtra={[
          renderPreviewButton(),
          shouldShowDelete ? (
            <Delete
              key={'delete-button'}
              entityDescription={deleteEntityDescription}
              onDelete={handleDelete}
            />
          ) : null,
        ]}
        isDirty
        backRoute={location.state?.pathname ?? '/packs'}
        isValid={canSubmit}
        saveDisabled={!canSubmit}
        isLoading={isLoading}
        headerMenu={!isCreatePage && renderMenu()}
      >
        <Content>
          {isDataLoaded ? (
            <Form
              form={form}
              className="pack-content-form"
              layout="vertical"
              onFinish={onFinish}
              initialValues={initialValues}
              onFieldsChange={(_, allFields) => {
                const hasFormErrors = allFields.some(
                  (field) => field.errors && field.errors.length > 0
                );

                setCanSubmit(
                  !hasFormErrors &&
                    allFields.every((field) => !field.errors?.length)
                );
              }}
            >
              <PackForm itemName={menu.name} />
            </Form>
          ) : (
            <Spinner />
          )}
        </Content>
      </SDetailLayout>

      <Modal
        className="pack-switch-modal"
        open={showModal}
        onCancel={() => {
          setShowModal(false);
          setPendingMenu(null);
        }}
        footer={
          <div className="custom-footer">
            <div>
              <Button
                onClick={() => {
                  setShowModal(false);
                  setPendingMenu(null);
                }}
              >
                Cancel
              </Button>
            </div>

            <div className="right-buttons">
              <Button onClick={handleModalDiscard}>Discard & Switch</Button>
              <Button
                type="primary"
                disabled={!canSubmit}
                onClick={handleModalSave}
              >
                Save & Switch
              </Button>
            </div>
          </div>
        }
        closable
        title="Unsaved Changes"
      >
        You have unsaved changes. Do you want to save before switching tabs?
      </Modal>
    </Layout>
  );
};
